import { createViewPlanning } from '../api';
import { glossaire_type_de_vue } from 'ressources/glossaires/glossaire_type_de_vue';

// Utilitaires pour générer les configurations par défaut des plannings

/**
 * Modes par défaut pour Planning Base de Vie
 */
export const getDefaultModesBaseVie = () => [
  { id: 4, name: 'OFF', color: '#FF5722' },
  { id: 1, name: 'Hors gel', color: '#FFBF00' },
  { id: 2, name: 'Eco', color: '#4CAF50' },
  { id: 3, name: 'Confort', color: '#2196F3' },
];

/**
 * Modes par défaut pour Planning Électrovanne
 */
export const getDefaultModesElectrovanne = () => [
  { id: 0, name: 'Ouvert', color: '#4789BC' },
  { id: 1, name: 'Fermé', color: '#A6E5EB' },
];

/**
 * Configuration par défaut pour Base de Vie
 * Tous les jours: OFF de 00h à 06h, puis Hors gel de 06h à 22h, puis OFF
 */
export const getDefaultConfigBaseVie = () => ({
  function_id: 1,
  group_taches: 'base_vie_management',
  task_type: 'maintenance',
  default_schedule: {
    // Configuration simple: 7h-17h en mode Eco du lundi au vendredi
    weekdays: '0,1,2,3,4,5,6', // Lundi à vendredi
    start_hour: '00', // 7h du matin
    end_hour: '24', // 17h
    weekend_mode: 'OFF', // Week-end en mode OFF
  },
  sensor_behavior: 'monitoring',
  priority: 'medium',
  // Timeline par défaut (48 segments de 30min chacun)
  defaultTimelineMatrix: Array.from({ length: 7 }, (_) => {
    return Array.from({ length: 48 }, (_) => {
      return { id: 4, name: 'OFF', color: '#FF5722' };
    });
  }),
  // Marqueurs par défaut (changements de mode)
  defaultMarkerMatrix: Array.from({ length: 7 }, (_) => {
    return [0]; // 7h (14 = 7*2) et 17h (34 = 17*2)
  }),
});

/**
 * Configuration par défaut pour Électrovanne
 * Irrigation le matin: Ouvert de 6h à 8h les jours pairs
 */
export const getDefaultConfigElectrovanne = () => ({
  function_id: 2,
  group_taches: 'irrigation_control',
  task_type: 'irrigation',
  default_schedule: {
    // Irrigation tous les 2 jours le matin
    irrigation_days: '0,2,4,6', // Dimanche, mardi, jeudi, samedi
    start_hour: '06', // 6h du matin
    duration_hours: '2', // 2 heures d'irrigation
    rest_mode: 'Fermé', // Fermé le reste du temps
  },
  sensor_behavior: 'trigger',
  valve_duration: 120, // 2 heures en minutes
  priority: 'high',
  // Timeline par défaut (48 segments de 30min chacun)
  defaultTimelineMatrix: Array.from({ length: 7 }, (_, dayIndex) => {
    // Jours d'irrigation: ouvert de 6h à 8h, fermé sinon
    if ([0, 2, 4, 6].includes(dayIndex)) {
      return Array.from({ length: 48 }, (_, segmentIndex) => {
        const hour = Math.floor(segmentIndex / 2);
        if (hour >= 6 && hour < 8) {
          return { id: 0, name: 'Ouvert', color: '#4789BC' };
        }
        return { id: 1, name: 'Fermé', color: '#A6E5EB' };
      });
    }
    // Autres jours: tout fermé
    return Array.from({ length: 48 }, () => ({
      id: 1,
      name: 'Fermé',
      color: '#A6E5EB',
    }));
  }),
  // Marqueurs par défaut
  defaultMarkerMatrix: Array.from({ length: 7 }, (_, dayIndex) => {
    if ([0, 2, 4, 6].includes(dayIndex)) {
      return [12, 16]; // 6h (12 = 6*2) et 8h (16 = 8*2)
    }
    return []; // Pas de changements les autres jours
  }),
});

/**
 * Génère les tâches par défaut basées sur la configuration et la timeline
 * @param {Object} planningData - Données du formulaire avec defaultConfig
 * @returns {Array} Tableau de tâches configurées
 */

export const generateDefaultTasks = (planningData) => {
  const { defaultConfig, selectedSensors, view_name, project_id } =
    planningData;
  const tasks = [];

  // Créer un squelette de tâche
  const createTaskSkeleton = () => ({
    name: view_name || 'Planning par défaut',
    type: 'planning_schedule',
    function_id: defaultConfig.function_id,
    group_taches: defaultConfig.group_taches,
    cron_year: '*',
    cron_month: '*',
    cron_day: '*',
    cron_week: '*',
    cron_day_of_week: '*',
    cron_hour: '0',
    cron_minute: '0',
    cron_second: '0',
    cron_start_day: new Date().toISOString().split('T')[0],
    cron_end_date: new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    )
      .toISOString()
      .split('T')[0], // Un an à partir d'aujourd'hui
    view_id: 0, // Sera défini par le backend
    state: 1,
    sensors_list: selectedSensors
      ? selectedSensors.map((s) => s.sensor_id).join(',')
      : '',
    id_arguments: generateDefaultArguments(defaultConfig, selectedSensors).map(
      (arg) => ({
        Position_in_arg: arg.Position_in_arg,
        Value: arg.Value,
      })
    ),
  });

  // Générer des tâches basées sur les marqueurs par défaut
  defaultConfig.defaultMarkerMatrix.forEach((markers, dayIndex) => {
    if (markers.length === 0) return; // Pas de tâches ce jour-là

    const timeline = defaultConfig.defaultTimelineMatrix[dayIndex];

    // Tâche pour le début de la journée (segment 0)
    if (!markers.includes(0)) {
      tasks.push({
        ...createTaskSkeleton(),
        name: `${view_name} - ${getDayName(dayIndex)}`,
        cron_day_of_week: dayIndex.toString(),
        cron_hour: '0',
        cron_minute: '0',
        id_arguments: [
          {
            Position_in_arg: 1,
            Value: timeline[0].id,
          },
        ],
      });
    }

    // Tâches pour chaque marqueur
    markers.forEach((marker, index) => {
      tasks.push({
        ...createTaskSkeleton(),
        name: `${view_name} - ${getDayName(dayIndex)} - ${getTimeFromSegment(
          marker
        )}`,
        cron_day_of_week: dayIndex.toString(),
        cron_hour: Math.floor(marker / 2).toString(),
        cron_minute: marker % 2 === 0 ? '0' : '30',
        id_arguments: [
          {
            Position_in_arg: 1,
            Value: timeline[marker].id.toString(),
          },
        ],
      });
    });
  });

  return tasks;
};

/**
 * Génère les arguments par défaut basés sur le type de planning
 * @param {Object} defaultConfig - Configuration par défaut
 * @param {Array} selectedSensors - Capteurs sélectionnés
 * @returns {Array} Tableau d'arguments
 */
const generateDefaultArguments = (defaultConfig, selectedSensors) => {
  const args = [];

  // Argument principal: comportement des capteurs
  if (defaultConfig.sensor_behavior) {
    args.push({
      Position_in_arg: 1,
      Value: defaultConfig.sensor_behavior.toString(),
    });
  }

  // Pour électrovanne: durée de la vanne
  if (defaultConfig.valve_duration) {
    args.push({
      Position_in_arg: 2,
      Value: defaultConfig.valve_duration.toString(),
    });
  }

  // Arguments spécifiques des capteurs
  if (selectedSensors && selectedSensors.length > 0) {
    selectedSensors.forEach((sensor, index) => {
      args.push({
        Position_in_arg: args.length + 1,
        Value: sensor.sensor_id.toString(),
      });
    });
  }

  return args;
};

/**
 * Convertit un numéro de segment en heure lisible
 * @param {number} segment - Numéro du segment (0-47)
 * @returns {string} Heure au format "HH:MM"
 */
const getTimeFromSegment = (segment) => {
  const hour = Math.floor(segment / 2);
  const minute = segment % 2 === 0 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minute}`;
};

/**
 * Convertit un numéro de jour en nom du jour
 * @param {number} dayNumber - Numéro du jour (0=Dimanche, 1=Lundi, etc.)
 * @returns {string} Nom du jour
 */
const getDayName = (dayNumber) => {
  const days = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ];
  return days[dayNumber] || 'Jour inconnu';
};

/**
 * Crée le payload final pour l'API de planning
 * @param {Object} planningData - Données du formulaire avec defaultConfig
 * @returns {Object} Payload pour API
 */
export const createPlanningPayload = (planningData) => {
  const { project_id, defaultConfig } = planningData;

  if (!defaultConfig) {
    console.warn(
      "Aucune configuration par défaut fournie, création d'un payload minimal"
    );
    return {
      project_id: parseInt(project_id),
      tasks: [],
    };
  }
  return {
    project_id: parseInt(project_id),
    tasks: generateDefaultTasks(planningData),
  };
};

/**
 * Fonction pour créer un planning via l'API
 * @param {Object} formData - Données du formulaire
 * @returns {Promise} - Réponse de l'API
 */
export const createPlanning = async (formData) => {
  const payload = createPlanningPayload(formData);
  return await createViewPlanning(payload);
};

/**
 * Retourne la configuration par défaut basée sur le type de vue
 * @param {number} viewType - Type de vue (6 = base_vie, 5 = electrovanne)
 * @returns {Object} - Configuration par défaut
 */
export const getDefaultConfigByType = (viewType) => {
  switch (viewType) {
    case glossaire_type_de_vue.planning_base_vie: // planning_base_vie
      return getDefaultConfigBaseVie();
    case glossaire_type_de_vue.planning_electrovanne: // planning_electrovanne
      return getDefaultConfigElectrovanne();
    default:
      console.warn(`Type de vue ${viewType} non reconnu`);
      return null;
  }
};
